import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "../lib/api";
import { notifyQuotaExhausted } from "../lib/quota";
import { toast } from "sonner";
import { FiloOffer } from "../components/FiloOffer";
import { useAuth } from "../context/AuthContext";
import { MarkdownText } from "../components/MarkdownText";
import { CopyReadingButton } from "../components/CopyReadingButton";
import { DeleteReadingButton } from "../components/DeleteReadingButton";
import { ReadingActions } from "../components/ReadingActions";
import { ReadingAlerts } from "../components/ReadingAlerts";
import { FeedbackStelle } from "../components/FeedbackStelle";
import { FiguraRiconosciuta, FiguraInvite } from "../components/FiguraRiconosciuta";
import { markReadingCompleted } from "../components/ReviewPrompt";
import { MoonStar, Sparkles, Square, Send, Loader2 } from "lucide-react";
import { CHAOS_CFG, createChaos, chaosStep, finalizeChaos, SHUFFLE_OPS } from "../lib/chaosDeck";

const SUIT_ICON = { Cuori: "♥", Quadri: "♦", Fiori: "♣", Picche: "♠" };
const SUIT_STYLE = { Cuori: "text-rose-400", Quadri: "text-rose-300", Fiori: "text-purple-300", Picche: "text-purple-200" };

const PIA_SUIT_STYLE = {
  Denari: "border-amber-500/50 text-amber-300",
  Coppe: "border-rose-500/50 text-rose-300",
  Bastoni: "border-emerald-500/50 text-emerald-300",
  Spade: "border-sky-500/40 text-sky-300",
  Oros: "border-amber-500/50 text-amber-300",
  Copas: "border-rose-500/50 text-rose-300",
  Bastos: "border-emerald-500/50 text-emerald-300",
  Espadas: "border-sky-500/40 text-sky-300",
};

const DECK_CFG = {
  "vera-sibilla": {
    title: "Sibille", eyebrow: null, mark: "sibille", redirect: "/lettura",
    loginText: "Per consultare le carte è necessario accedere o creare un account.",
  },
  "piacentine": {
    title: "Piacentine", eyebrow: "Metodo di lettura", mark: "piacentine", redirect: "/piacentine",
    loginText: "Per consultare le Piacentine è necessario accedere o creare un account.",
  },
  "napoletane": {
    title: "Napoletane", eyebrow: "Metodo di lettura", mark: "napoletane", redirect: "/napoletane",
    loginText: "Per consultare le Napoletane è necessario accedere o creare un account.",
  },
  "baraja": {
    title: "Baraja Española", eyebrow: "Metodo di lettura", mark: "baraja", redirect: "/baraja",
    loginText: "Per consultare la Baraja Española è necessario accedere o creare un account.",
  },
  "tarocchi": {
    title: "Tarocchi", eyebrow: "Metodo di lettura", mark: "tarocchi", redirect: "/tarocchi",
    loginText: "Per consultare i Tarocchi è necessario accedere o creare un account.",
  },
  "stesa26": {
    title: "Stesa 26", eyebrow: "Il metodo delle 26 carte", mark: "stesa26", redirect: "/stesa26",
    loginText: "Per consultare la Stesa 26 è necessario accedere o creare un account.",
  },
};

// Finestra di scelta del mazzo, solo per i Tarocchi (manuale Laura Tuan)
const TAROCCHI_SUBSETS = [
  { id: "maggiore", label: "Arcani Maggiori", sub: "22 carte" },
  { id: "minore", label: "Arcani Minori", sub: "56 carte" },
  { id: "completo", label: "Mazzo Intero", sub: "78 carte" },
];

const METHODS = [
  { id: "tre", label: "Tre carte", sub: "Passato · Presente · Futuro" },
  { id: "croce", label: "Croce", sub: "Analisi della situazione" },
  { id: "settenaria", label: "Settenaria", sub: "7 carte · Lettura completa" },
];

// Stesure dei Tarocchi secondo il manuale: Una carta, Oracolo due carte, Tre carte, Settenaria, Sì o No (niente Croce)
// "due" e "siono" richiedono i soli Arcani Maggiori (regola del manuale): selezionabili solo col mazzo da 22
const TAROCCHI_METHODS = [
  { id: "una", label: "Una carta", sub: "La risposta" },
  { id: "due", label: "Due carte", sub: "Situazione · Esito", needs: "maggiore" },
  { id: "tre", label: "Tre carte", sub: "Passato · Presente · Futuro" },
  { id: "settenaria", label: "Settenaria", sub: "7 carte · Lettura completa" },
  { id: "siono", label: "Sì o No", sub: "Solo Maggiori · Verdetto", needs: "maggiore" },
];

// Dorso definitivo Arcana: immagine intera, proporzioni corrette, nessun bordo chiaro residuo
const CardBack = ({ className = "", style = {}, innerRef }) => (
  <div
    ref={innerRef}
    style={{ backgroundImage: "url('/dorso-arcana.jpg')", backgroundSize: "cover", backgroundPosition: "center", ...style }}
    className={`rounded-md overflow-hidden bg-[#1b0b2e] shadow-lg shadow-black/40 ${className}`}
  >
    <img src="/dorso-arcana.jpg" alt="" draggable={false} className="w-full h-full object-fill select-none pointer-events-none block" />
  </div>
);

const CardFace = ({ card, deck }) => {
  let inner;
  if (deck === "tarocchi") {
    if (card.image_url) {
      inner = (
        <div className={`flip-in w-full rounded-md overflow-hidden ${card.reversed ? "card-rev" : ""}`} data-testid={`smazzo-card-face-${card.name}`}>
          <img src={card.image_url} alt={card.name} loading="lazy" decoding="async" className="w-full h-auto object-contain select-none pointer-events-none" draggable={false} />
        </div>
      );
    } else {
      inner = (
        <div className={`flip-in w-full aspect-[3/4.4] rounded-md border-2 bg-[#150826] p-1 flex flex-col items-center justify-between border-amber-500/50 text-amber-200 ${card.reversed ? "card-rev" : ""}`}>
          <span className="text-[8px] font-cinzel self-start">{card.suit === "Arcani Maggiori" ? card.title.replace("Arcano ", "") : card.suit.slice(0, 3).toUpperCase()}</span>
          <span className="font-display text-[9px] text-center leading-tight text-purple-50">{card.name}</span>
          <span className="text-[8px] font-cinzel self-end rotate-180">{card.suit === "Arcani Maggiori" ? card.title.replace("Arcano ", "") : card.suit.slice(0, 3).toUpperCase()}</span>
        </div>
      );
    }
  } else if (deck === "piacentine" || deck === "stesa26" || deck === "napoletane" || deck === "baraja") {
    if (card.image_url) {
      inner = (
        <div className={`flip-in w-full rounded-md overflow-hidden ${card.reversed ? "card-rev" : ""}`} data-testid={`smazzo-card-face-${card.name}`}>
          <img src={card.image_url} alt={card.name} loading="lazy" decoding="async" className="w-full h-auto object-contain select-none pointer-events-none" draggable={false} />
        </div>
      );
    } else {
      inner = (
        <div className={`flip-in w-full aspect-[3/4.4] rounded-md border-2 bg-[#0f0617] p-1 flex flex-col items-center justify-between ${card.reversed ? "card-rev" : ""} ${PIA_SUIT_STYLE[card.suit] || "border-[#4a2175] text-purple-200"}`}>
          <span className="text-[8px] font-cinzel self-start">{card.number}</span>
          <span className="font-display text-[9px] text-center leading-tight text-purple-50">{card.name}</span>
          <span className="text-[8px] font-cinzel self-end rotate-180">{card.number}</span>
        </div>
      );
    }
  } else if (card.image_url) {
    inner = (
      <div className={`flip-in w-full aspect-[12/25] rounded-md overflow-hidden ${card.reversed ? "card-rev" : ""}`} data-testid={`smazzo-card-face-${card.name}`}>
        <img src={card.image_url} alt={card.name} loading="lazy" decoding="async" className="w-full h-full object-contain select-none pointer-events-none" draggable={false} />
      </div>
    );
  } else {
    inner = (
      <div className={`flip-in w-full aspect-[3/4.4] rounded-md border bg-[#0f0617] border-amber-500/40 p-1.5 flex flex-col items-center justify-between ${card.reversed ? "card-rev" : ""}`}>
        <span className="text-[9px] font-cinzel text-amber-300/80 self-start">{card.number}</span>
        <span className="font-display text-[10px] sm:text-xs text-center leading-tight text-purple-50">{card.name}</span>
        <span className={`text-sm ${SUIT_STYLE[card.suit] || "text-purple-200"}`}>{SUIT_ICON[card.suit]}</span>
      </div>
    );
  }
  return (
    <div className="relative w-full">
      {inner}
      {typeof card.reversed === "boolean" && (
        <span
          data-testid={`orient-dot-${card.reversed ? "rovesciata" : "dritta"}-${card.name}`}
          title={card.reversed ? "Carta rovesciata" : "Carta dritta"}
          className={`absolute -bottom-1 left-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full z-10 ring-2 ring-[#150826] ${card.reversed ? "bg-purple-500" : "bg-amber-400"}`}
        />
      )}
    </div>
  );
};

// Croce: Cuore al centro, Ostacolo a sinistra, Aiuto a destra, Evoluzione sotto
const CROCE_CELL = [
  "col-start-2 row-start-1",
  "col-start-1 row-start-2",
  "col-start-3 row-start-2",
  "col-start-2 row-start-3",
];

export default function SmazzoStesa({ deck = "vera-sibilla", embedded = false, preset = null, onEmbeddedDone = null, onClose = null }) {
  const cfg = DECK_CFG[deck];
  const { user, loading } = useAuth();
  const [phase, setPhase] = useState(preset ? (preset.spread === "siono" && !preset.sionoNumber ? "siono-num" : "avvio") : "scelta"); // scelta|form|manuale|siono-num|avvio|orienta|ricomponi|mischia|smazzo|taglio|stesa|lettura|done
  const [mode, setMode] = useState(preset ? "arcana" : null); // "arcana" (stesa guidata) | "mia" (mazzo personale)
  const [deckCards, setDeckCards] = useState([]);
  const [positions, setPositions] = useState([]);
  const [manualSlots, setManualSlots] = useState([]); // [{id, reversed}|null] nell'ordine delle posizioni
  const [sionoRow, setSionoRow] = useState(null); // 1..6 | "acc"
  const [pickerSlot, setPickerSlot] = useState(null);
  const [avvisi, setAvvisi] = useState(null);
  const [figura, setFigura] = useState(null);
  const [figuraOpen, setFiguraOpen] = useState(false);
  const [question, setQuestion] = useState(preset?.question || "");
  const [spread, setSpread] = useState(preset?.spread || null);
  const [subset, setSubset] = useState(preset?.subset || null); // tarocchi: maggiore|minore|completo
  const [sionoNumber, setSionoNumber] = useState(preset?.sionoNumber || null); // tarocchi siono: numero 1-22 scelto dal consultante
  const [session, setSession] = useState(null);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(false);
  const [giri, setGiri] = useState(0);
  const [tick, setTick] = useState(0);
  const [cutIndex, setCutIndex] = useState(null);
  const [dealt, setDealt] = useState(0);
  const [cards, setCards] = useState([]);
  const [text, setText] = useState("");
  const [readingId, setReadingId] = useState(null);
  const [nextQuestion, setNextQuestion] = useState("");
  const [apprQuestion, setApprQuestion] = useState("");
  const [apprThread, setApprThread] = useState([]);
  const [apprBusy, setApprBusy] = useState(false);
  const [apprRemaining, setApprRemaining] = useState(2);
  const [apprChatId, setApprChatId] = useState(null);
  const [crudaBusy, setCrudaBusy] = useState(false);
  const [crudaText, setCrudaText] = useState("");
  const [crudaDeclined, setCrudaDeclined] = useState(false);
  const [crudaError, setCrudaError] = useState("");

  useEffect(() => {
    setApprQuestion(""); setApprThread([]); setApprBusy(false); setApprRemaining(2); setApprChatId(null);
    setCrudaText(""); setCrudaDeclined(false); setCrudaError(""); setCrudaBusy(false);
  }, [readingId]);
  const [linkedCtx, setLinkedCtx] = useState(null);

  const deckRef = useRef([]); // stato REALE del mazzo: [{id, reversed}], indice 0 = cima
  const cardMapRef = useRef({});
  const chaosRef = useRef(null);
  const ptrRef = useRef({ down: false, x: 0, y: 0, W: 0, H: 0 });
  const pileBoxRef = useRef(null);
  const cardElsRef = useRef({});
  const finalizeSentRef = useRef(false);

  const n = deckRef.current.length;

  // ---------- modalità «Ho già fatto la mia stesa»: inserimento manuale delle carte ----------
  const startManualEntry = async () => {
    if (starting) return;
    setStarting(true);
    setError("");
    try {
      const params = new URLSearchParams({ spread: isS26 ? "stesa26" : spread });
      if (deck === "tarocchi" && subset) params.set("subset", subset);
      const { data } = await api.get(`/smazzo/decks/${deck}/cards?${params.toString()}`);
      setDeckCards(data.cards);
      setPositions(data.positions || []);
      const nSlots = isS26 ? 26 : (deck === "tarocchi" && spread === "siono" ? 3 : (data.positions || []).length + 2);
      const slots = Array(nSlots).fill(null);
      if (deck === "tarocchi" && spread === "siono") {
        const lama = data.cards.find((c) => c.number === sionoNumber);
        if (lama) slots[2] = { id: lama.id, reversed: false }; // la lama deriva dal numero scelto
      }
      setManualSlots(slots);
      setSionoRow(null);
      setPickerSlot(0);
      setPhase("manuale");
    } catch (e) {
      setError("Qualcosa è andato storto. Riprova.");
    } finally {
      setStarting(false);
    }
  };

  const slotLabel = (i) => {
    if (isS26) return positions[i] || `Posizione ${i}`;
    if (deck === "tarocchi" && spread === "siono")
      return ["Prima carta del taglio", "Seconda carta del taglio", `Lama scelta (n. ${sionoNumber})`][i];
    if (i < 2) return ["Smazzo — prima carta del taglio", "Smazzo — seconda carta del taglio"][i];
    return positions[i - 2] || "";
  };

  const assignCard = (cardId) => {
    if (pickerSlot == null) return;
    const next = [...manualSlots];
    next[pickerSlot] = { id: cardId, reversed: next[pickerSlot] ? next[pickerSlot].reversed : false };
    setManualSlots(next);
    const after = next.findIndex((s, i) => !s && i > pickerSlot);
    const any = next.findIndex((s) => !s);
    setPickerSlot(after !== -1 ? after : (any !== -1 ? any : null));
  };

  const toggleRev = (i) => {
    const next = [...manualSlots];
    if (next[i]) next[i] = { ...next[i], reversed: !next[i].reversed };
    setManualSlots(next);
  };

  const manualComplete = manualSlots.length > 0 && manualSlots.every(Boolean) &&
    !(deck === "tarocchi" && spread === "siono" && sionoRow == null);

  const ManualSlot = ({ idx }) => {
    const sel = manualSlots[idx];
    const card = sel ? deckCards.find((c) => c.id === sel.id) : null;
    const locked = deck === "tarocchi" && spread === "siono" && idx === 2; // la lama deriva dal numero scelto
    return (
      <div>
        <button onClick={() => !locked && setPickerSlot(idx)} data-testid={`manuale-slot-${idx}`}
          className={`w-full rounded-md transition-all ${pickerSlot === idx ? "ring-2 ring-amber-400/70" : ""} ${locked ? "cursor-default" : ""}`}>
          {card ? (
            <CardFace card={{ ...card, reversed: !!sel.reversed, position: slotLabel(idx) }} deck={deck} />
          ) : (
            <div className="aspect-[3/4.4] rounded-md border border-dashed border-[#4a2175] bg-[#180a26]/60 flex items-center justify-center p-1">
              <span className="text-[9px] text-purple-300/50 text-center leading-tight">{slotLabel(idx)}</span>
            </div>
          )}
        </button>
        {!isS26 && <p className="text-[9px] uppercase tracking-wider text-purple-300/50 mt-1 text-center">{slotLabel(idx)}</p>}
        {!isS26 && card && (
          <button onClick={() => toggleRev(idx)} data-testid={`manuale-rev-${idx}`}
            className={`block mx-auto w-fit mt-1 text-[10px] px-2 py-0.5 rounded-full border transition-colors ${sel.reversed ? "border-fuchsia-500/40 text-fuchsia-300" : "border-amber-500/40 text-amber-300"}`}>
            {sel.reversed ? "Rovesciata" : "Dritta"}
          </button>
        )}
      </div>
    );
  };

  const confirmManual = async () => {
    if (starting || !manualComplete) return; // protezione doppio invio: un solo addebito
    setStarting(true);
    setError("");
    try {
      const payload = {
        spread: isS26 ? "stesa26" : spread, question, deck,
        ...(deck === "tarocchi" && subset ? { subset } : {}),
        ...(sionoNumber ? { siono_number: sionoNumber } : {}),
        ...(linkedCtx ? { linked: linkedCtx } : {}),
        manual: {
          cards: manualSlots.map((s) => ({ id: s.id, reversed: isS26 ? false : !!s.reversed })),
          ...(deck === "tarocchi" && spread === "siono"
            ? (sionoRow === "acc" ? { siono_accantonata: true } : { siono_row: sionoRow })
            : {}),
        },
      };
      const { data } = await api.post("/smazzo/sessions", payload);
      setSession(data);
      const drawn = data.drawn_cards || [];
      cardMapRef.current = Object.fromEntries(drawn.map((c, i) => [`m${i}`, c]));
      setCards(drawn);
      setDealt(drawn.length);
      await interpretStream(data.id);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "email_non_verificata") { setError(d.message); }
      else if (d?.code === "quota_esaurita") {
        if (d?.message && d.message.includes("richiede")) setError(d.message);
        else notifyQuotaExhausted();
      } else setError(typeof d === "string" ? d : "Qualcosa è andato storto. Riprova.");
    } finally {
      setStarting(false);
    }
  };

  // ---------- fase 1: avvio ----------
  const start = async () => {
    if (mode === "mia") { startManualEntry(); return; }
    if (starting) return; // protezione doppio invio: una sola consultazione (e un solo addebito) per volta
    setStarting(true);
    setError("");
    try {
      const { data } = await api.post("/smazzo/sessions", { spread: isS26 ? "stesa26" : spread, question, deck, ...(subset ? { subset } : {}), ...(sionoNumber ? { siono_number: sionoNumber } : {}), ...(linkedCtx ? { linked: linkedCtx } : {}), ...(embedded ? { private: true } : {}) });
      setSession(data);
      cardMapRef.current = Object.fromEntries(data.cards.map((c) => [c.id, c]));
      // REGOLA PRINCIPALE: tutte le carte partono dritte, sempre
      deckRef.current = data.cards.map((c) => ({ id: c.id, reversed: false }));
      setPhase(isS26 ? "mischia" : "orienta");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "quota_esaurita") {
        // messaggi dedicati (es. "richiede 2 consultazioni: oggi te ne resta 1") mostrati tali quali
        if (d?.message && d.message.includes("richiede")) setError(d.message);
        else notifyQuotaExhausted();
      }
      else setError(typeof d === "string" ? d : (d?.message || "Qualcosa è andato storto. Riprova."));
    } finally {
      setStarting(false);
    }
  };

  // Modalità incorporata (chat «La mia memoria»): i parametri arrivano dal wizard e il rito parte da solo
  const autoStartRef = useRef(false);
  useEffect(() => {
    if (!preset || phase !== "avvio" || autoStartRef.current) return;
    autoStartRef.current = true;
    start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // ---------- fase 2: il caos — mucchietto largo, tre giri lenti ----------
  useEffect(() => {
    if (phase !== "orienta" || !pileBoxRef.current) return;
    const r = pileBoxRef.current.getBoundingClientRect();
    chaosRef.current = createChaos(deckRef.current.map((c) => c.id), r.width, r.height);
    paintChaos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const paintChaos = () => {
    const st = chaosRef.current;
    if (!st) return;
    for (const c of st.cards) {
      const el = cardElsRef.current[c.id];
      if (!el) continue;
      el.style.transition = "none";
      el.style.transform = `translate(${c.x}px, ${c.y}px) rotate(${c.angle}deg)`;
      el.style.zIndex = c.z % 500;
    }
  };

  const onPointerDown = (e) => {
    if (phase !== "orienta" || !chaosRef.current) return;
    const r = pileBoxRef.current.getBoundingClientRect();
    ptrRef.current = { down: true, x: e.clientX - r.left, y: e.clientY - r.top, W: r.width, H: r.height };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    const p = ptrRef.current;
    if (!p.down || phase !== "orienta" || !chaosRef.current) return;
    const r = pileBoxRef.current.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    const st = chaosRef.current;
    const flips = chaosStep(st, x, y, x - p.x, y - p.y, r.width / 2, r.height / 2);
    p.x = x;
    p.y = y;
    paintChaos();
    // piccolo impulso visivo dove una carta si e' fisicamente capovolta
    for (const id of flips) {
      const el = cardElsRef.current[id];
      el?.animate(
        [{ transform: el.style.transform + " scale(1)" }, { transform: el.style.transform + " scale(1.18)" }, { transform: el.style.transform + " scale(1)" }],
        { duration: 320, easing: "ease-out" }
      );
    }
    setGiri(st.laps);
    if (st.laps >= 3) finishChaos();
  };

  const onPointerUp = () => {
    ptrRef.current.down = false;
    if (chaosRef.current) chaosRef.current.prevAng = null;
  };

  const finishChaos = () => {
    if (phase !== "orienta") return;
    ptrRef.current.down = false;
    // il mazzo finale e' lo STESSO mazzo trasformato dal caos: identita', ordine e orientamenti reali
    const newDeck = finalizeChaos(chaosRef.current);
    deckRef.current = newDeck;
    setPhase("ricomponi");
    const r = pileBoxRef.current.getBoundingClientRect();
    const st = chaosRef.current;
    for (let idx = 0; idx < newDeck.length; idx++) {
      const card = newDeck[idx];
      const c = st.cards.find((x) => x.id === card.id);
      const el = cardElsRef.current[card.id];
      if (!el || !c) continue;
      c.x = r.width / 2 - CHAOS_CFG.CARD_W / 2 + (idx - newDeck.length / 2) * 0.4;
      c.y = r.height / 2 - CHAOS_CFG.CARD_H / 2 + (idx - newDeck.length / 2) * 0.3;
      c.angle = (card.reversed ? 180 : 0) + ((idx % 5) - 2) * 1.2;
      el.style.transition = "transform 0.85s cubic-bezier(0.22, 1, 0.36, 1)";
      el.style.transform = `translate(${c.x}px, ${c.y}px) rotate(${c.angle}deg)`;
      el.style.zIndex = idx;
    }
    setTimeout(() => setPhase("mischia"), 1000);
  };

  // ---------- fase 3: mischiatura reale fino a STOP (l'orientamento segue la carta) ----------
  useEffect(() => {
    if (phase !== "mischia") return;
    const iv = setInterval(() => {
      const op = SHUFFLE_OPS[Math.floor(Math.random() * SHUFFLE_OPS.length)];
      deckRef.current = op(deckRef.current);
      setTick((t) => t + 1);
    }, 750);
    return () => clearInterval(iv);
  }, [phase]);

  // ---------- fase 4/5: smazzo e stesa ----------
  const beginCut = (i) => {
    if (phase !== "smazzo" || i < 1 || i > n - 2) return;
    setCutIndex(i);
    setPhase("taglio");
    setTimeout(() => setPhase("stesa"), 2400);
  };

  const isS26 = deck === "stesa26";
  const orderNow = deckRef.current;
  const cutIds = cutIndex != null ? (isS26 ? [orderNow[cutIndex].id] : [orderNow[cutIndex - 1].id, orderNow[cutIndex].id]) : [];
  const count = session?.count || 0;
  const recomposed = cutIndex != null
    ? (isS26 ? [...orderNow.slice(cutIndex + 1), ...orderNow.slice(0, cutIndex)] : [...orderNow.slice(cutIndex + 1), ...orderNow.slice(0, cutIndex - 1)])
    : [];
  const drawnIds = mode === "mia"
    ? cards.map((_, i) => `m${i}`)
    : (cutIndex != null ? [...cutIds, ...recomposed.slice(0, isS26 ? count - 1 : count).map((c) => c.id)] : []);
  const totalCards = mode === "mia" ? cards.length : (isS26 ? count : count + 2);
  const firstOut = isS26 ? 1 : 2;
  const revById = Object.fromEntries(orderNow.map((c) => [c.id, c.reversed]));

  useEffect(() => {
    if (phase !== "stesa") return;
    setDealt(firstOut); // le carte fuori dal mazzo (smazzo/fuori quadro) sono già scoperte
    const iv = setInterval(() => {
      setDealt((d) => {
        if (d >= totalCards) {
          clearInterval(iv);
          return d;
        }
        return d + 1;
      });
    }, 450);
    return () => clearInterval(iv);
  }, [phase, count]);

  useEffect(() => {
    if (phase === "stesa" && dealt >= totalCards && count > 0 && !finalizeSentRef.current) {
      finalizeSentRef.current = true;
      finalizeAndInterpret();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealt, phase, count]);

  const finalizeAndInterpret = async () => {
    try {
      const { data } = await api.post(`/smazzo/sessions/${session.id}/finalize`, {
        order: deckRef.current.map((c) => c.id),
        reversed: deckRef.current.map((c) => c.reversed),
        cut_index: cutIndex,
      });
      setCards(data.cards);
    } catch (e) {
      setError("Qualcosa è andato storto. Riprova.");
      setPhase(embedded ? "errore" : "form");
      return;
    }
    await interpretStream(session.id);
  };

  const interpretStream = async (sessionId) => {
    setPhase("lettura");
    setText("");
    setFigura(null);
    setFiguraOpen(false);
    let rid = null;
    try {
      const res = await fetch(`${API}/smazzo/sessions/${sessionId}/interpret`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(typeof d.detail === "string" ? d.detail : "Qualcosa è andato storto. Riprova.");
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data: ")) continue;
          const ev = JSON.parse(line.slice(6));
          if (ev.type === "meta") { rid = ev.reading_id; setReadingId(ev.reading_id); setAvvisi(ev.avvisi || null); setFigura(ev.figura || null); }
          else if (ev.type === "delta") setText((t) => t + ev.content);
          else if (ev.type === "error") throw new Error(ev.content);
        }
      }
      if (embedded) { onEmbeddedDone?.(rid); return; }
      setPhase("done");
      markReadingCompleted(cfg.mark);
    } catch (e) {
      setError(/network|failed to fetch/i.test(e?.message || "") ? "Connessione interrotta. Riprova tra poco: la consultazione non ti verrà addebitata di nuovo." : e.message);
      setPhase(embedded ? "errore" : "done");
    }
  };

  const reset = () => {
    setPhase("scelta");
    setMode(null);
    setDeckCards([]);
    setPositions([]);
    setManualSlots([]);
    setSionoRow(null);
    setPickerSlot(null);
    setSession(null);
    setQuestion("");
    setSpread(null);
    setSionoNumber(null);
    setGiri(0);
    setCutIndex(null);
    setDealt(0);
    setCards([]);
    setText("");
    setReadingId(null);
    setError("");
    setLinkedCtx(null);
    setCrudaText("");
    setCrudaDeclined(false);
    setCrudaError("");
    setCrudaBusy(false);
    chaosRef.current = null;
    finalizeSentRef.current = false;
  };

  const askCruda = async () => {
    if (crudaBusy || !session) return;
    setCrudaBusy(true);
    setCrudaError("");
    try {
      const { data } = await api.post(`/smazzo/sessions/${session.id}/cruda`);
      setCrudaText(data.text);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "quota_esaurita") {
        if (d?.message) setCrudaError(d.message);
        else notifyQuotaExhausted();
      } else setCrudaError(typeof d === "string" ? d : (d?.message || "Qualcosa è andato storto. Riprova."));
    } finally {
      setCrudaBusy(false);
    }
  };

  const sendAppr = async () => {
    const q = apprQuestion.trim();
    if (q.length < 3 || apprBusy || !readingId || apprRemaining <= 0) return;
    setApprBusy(true);
    try {
      let cid = apprChatId;
      if (!cid) {
        const { data } = await api.post("/assistant/open", { source_type: "lettura", source_id: readingId });
        cid = data.chat_id;
        setApprChatId(cid);
        setApprRemaining(data.remaining ?? 2);
        if (data.messages?.length) {
          const pairs = [];
          let cur = null;
          for (const m of data.messages) {
            if (m.role === "user") { cur = { q: m.content, a: "" }; pairs.push(cur); }
            else if (m.role === "assistant" && cur) { cur.a = m.content; }
          }
          setApprThread(pairs);
        }
      }
      setApprThread((t) => [...t, { q, a: "", streaming: true }]);
      setApprQuestion("");
      const { data } = await api.post("/assistant/message", { chat_id: cid, text: q });
      setApprThread((t) => t.map((m, i) => (i === t.length - 1 && m.q === q ? { ...m, a: data.reply, streaming: false } : m)));
      if (typeof data.remaining === "number") setApprRemaining(data.remaining);
    } catch (e) {
      setApprThread((t) => t.filter((m) => !(m.streaming && !m.a)));
      setApprQuestion(q);
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Arcana non è disponibile in questo momento. Riprova tra poco.");
    } finally {
      setApprBusy(false);
    }
  };

  const startLinkedQuestion = () => {
    const q = nextQuestion.trim();
    if (q.length < 5 || !text) return;
    const ctx = {
      question,
      answer: text,
      cards: (cards || []).map((c) => `${c.name}${c.reversed ? " (rovesciata)" : ""} — ${c.position}`),
    };
    reset();
    setLinkedCtx(ctx);
    setQuestion(q);
    setNextQuestion("");
  };

  if (loading) return null;

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="smazzo-login-required">
        <div className="mystic-card p-10">
          <MoonStar className="w-10 h-10 text-amber-400/60 mx-auto mb-5" />
          <h1 className="font-display text-3xl text-amber-100 mb-4">{cfg.title}</h1>
          <p className="text-purple-200/70 text-sm mb-8">{cfg.loginText}</p>
          <Link to={`/accedi?redirect=${cfg.redirect}`} data-testid="smazzo-login-btn" className="gold-btn inline-block px-8 py-3 text-sm">
            Accedi o registrati
          </Link>
        </div>
      </div>
    );
  }

  const pileHalf = Math.ceil(n / 2);

  return (
    <div className={embedded ? "px-1 py-2" : "max-w-3xl mx-auto px-4 lg:px-8 py-14 sm:py-16"} data-testid="smazzo-page">
      {!embedded && (
      <div className="text-center mb-10">
        {cfg.eyebrow && <p className="eyebrow">{cfg.eyebrow}</p>}
        <h1 className="font-display text-4xl sm:text-5xl text-amber-100 mt-4">{cfg.title}</h1>
      </div>
      )}

      {embedded && phase === "avvio" && (
        <div className="mystic-card p-7 text-center" data-testid="smazzo-avvio">
          <MoonStar className="w-7 h-7 text-amber-400/70 mx-auto mb-3 animate-pulse" />
          <p className="text-purple-200/70 text-sm">Preparo il mazzo…</p>
          {error && <p className="text-red-400 text-sm mt-3" data-testid="smazzo-error">{error}</p>}
          {error && (
            <button onClick={onClose} data-testid="smazzo-avvio-close" className="mt-4 text-[11px] text-purple-300/70 hover:text-amber-300 border border-[#4a2175] rounded-full px-4 py-1.5 transition-colors">
              Chiudi
            </button>
          )}
        </div>
      )}

      {embedded && phase === "siono-num" && (
        <div className="mystic-card p-7 sm:p-8" data-testid="smazzo-siono-num">
          <p className="eyebrow mb-4">Scegli il tuo numero, da 1 a 22</p>
          <p className="text-[11px] text-purple-300/50 mb-3">Come da manuale: il numero corrisponderà a un arcano, e la fila in cui cadrà darà il verdetto.</p>
          <div className="grid grid-cols-6 gap-1.5" data-testid="siono-number-picker-embedded">
            {Array.from({ length: 22 }, (_, i) => i + 1).map((num) => (
              <button
                key={num}
                onClick={() => setSionoNumber(num)}
                data-testid={`siono-num-${num}`}
                className={`py-2 rounded-md text-sm font-cinzel transition-all duration-200 border ${
                  sionoNumber === num
                    ? "border-amber-500/60 bg-amber-500/10 text-amber-200"
                    : "border-[#4a2175] bg-[#180a26] text-purple-200/80 hover:border-amber-500/30"
                }`}
              >
                {num}
              </button>
            ))}
          </div>
          <button
            onClick={() => setPhase("avvio")}
            disabled={!sionoNumber}
            data-testid="smazzo-siono-start"
            className="gold-btn w-full py-3 text-sm mt-5 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Sparkles className="w-4 h-4" /> Prendi il mazzo
          </button>
        </div>
      )}

      {embedded && phase === "errore" && (
        <div className="mystic-card p-7 text-center" data-testid="smazzo-errore">
          <p className="text-red-400 text-sm">{error || "Qualcosa è andato storto. Riprova."}</p>
          <button onClick={onClose} data-testid="smazzo-errore-close" className="mt-4 text-[11px] text-purple-300/70 hover:text-amber-300 border border-[#4a2175] rounded-full px-4 py-1.5 transition-colors">
            Chiudi
          </button>
        </div>
      )}

      {phase === "scelta" && (
        <div className="mystic-card p-7 sm:p-9" data-testid="smazzo-scelta">
          <h2 className="font-display text-2xl sm:text-3xl text-amber-100 text-center mb-8">Come vuoi fare la tua stesa?</h2>
          <div className="flex justify-center items-start gap-2 mb-8" aria-hidden="true">
            <CardBack className="w-[52px] h-[76px] -rotate-6 translate-y-1" />
            <CardBack className="w-[52px] h-[76px]" />
            <CardBack className="w-[52px] h-[76px] rotate-6 translate-y-1" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <button
              onClick={() => { setMode("arcana"); setPhase("form"); }}
              data-testid="scelta-stesa-arcana"
              className="rounded-lg border border-[#4a2175] bg-[#180a26] p-6 text-left transition-all duration-300 hover:border-amber-500/50 hover:-translate-y-0.5"
            >
              <span className="block font-display text-lg text-amber-100 mb-2">Voglio fare una stesa con Arcana</span>
              <span className="block text-xs text-purple-200/70 leading-relaxed">
                Scegli la tua domanda e lasciati guidare da Arcana nella tua consultazione.
              </span>
            </button>
            <button
              onClick={() => { setMode("mia"); setPhase("form"); }}
              data-testid="scelta-mia-stesa"
              className="rounded-lg border border-[#4a2175] bg-[#180a26] p-6 text-left transition-all duration-300 hover:border-amber-500/50 hover:-translate-y-0.5"
            >
              <span className="block font-display text-lg text-amber-100 mb-2">Ho già fatto la mia stesa</span>
              <span className="block text-xs text-purple-200/70 leading-relaxed">
                Hai già estratto le carte dal tuo mazzo? Inserisci le carte che hai estratto e Arcana interpreterà la tua stesa completa.
              </span>
            </button>
          </div>
        </div>
      )}

      {phase === "form" && (
        <div className="mystic-card p-7 sm:p-9 space-y-6" data-testid="smazzo-form">
          <div>
            <p className="eyebrow mb-4">La tua domanda</p>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Scrivi qui la tua domanda…"
              data-testid="smazzo-question-input"
              className="input-dark min-h-[110px] resize-y"
            />
          </div>
          {deck === "tarocchi" && (
            <div>
              <p className="eyebrow mb-4">Scegli il mazzo</p>
              <div className="grid grid-cols-3 gap-3" data-testid="tarocchi-subset-picker">
                {TAROCCHI_SUBSETS.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => {
                      setSubset(s.id);
                      // coerenza mazzo/stesa: i giochi riservati ai Maggiori cadono se il mazzo non li contiene
                      if (s.id !== "maggiore" && (spread === "due" || spread === "siono")) {
                        setSpread(null);
                        setSionoNumber(null);
                      }
                    }}
                    data-testid={`tarocchi-subset-${s.id}`}
                    className={`py-3 px-2 rounded-lg transition-all duration-300 border ${
                      subset === s.id
                        ? "border-amber-500/60 bg-amber-500/10 text-amber-200"
                        : "border-[#4a2175] bg-[#180a26] text-purple-200/80 hover:border-amber-500/30"
                    }`}
                  >
                    <span className="block text-[13px] leading-tight break-words">{s.label}</span>
                    <span className="block text-[10px] text-purple-300/60 mt-1">{s.sub}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {deck === "tarocchi" && spread === "siono" && (
            <div>
              <p className="eyebrow mb-4">Scegli il tuo numero, da 1 a 22</p>
              <p className="text-[11px] text-purple-300/50 mb-3">Come da manuale: il numero corrisponderà a un arcano, e la fila in cui cadrà darà il verdetto.</p>
              <div className="grid grid-cols-6 gap-1.5" data-testid="siono-number-picker">
                {Array.from({ length: 22 }, (_, i) => i + 1).map((num) => (
                  <button
                    key={num}
                    onClick={() => setSionoNumber(num)}
                    data-testid={`siono-num-${num}`}
                    className={`py-2 rounded-md text-sm font-cinzel transition-all duration-200 border ${
                      sionoNumber === num
                        ? "border-amber-500/60 bg-amber-500/10 text-amber-200"
                        : "border-[#4a2175] bg-[#180a26] text-purple-200/80 hover:border-amber-500/30"
                    }`}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>
          )}
          {deck !== "stesa26" && (
          <div>
            <p className="eyebrow mb-4">Scegli il metodo di stesura</p>
            <div className="grid grid-cols-2 gap-3">
              {(deck === "tarocchi" ? TAROCCHI_METHODS : METHODS).map((m) => {
                const locked = deck === "tarocchi" && m.needs === "maggiore" && subset !== "maggiore";
                return (
                <button
                  key={m.id}
                  onClick={() => !locked && setSpread(m.id)}
                  disabled={locked}
                  data-testid={`smazzo-method-${m.id}`}
                  className={`py-3 px-2 rounded-lg transition-all duration-300 border ${
                    locked
                      ? "border-[#4a2175]/40 bg-[#180a26]/50 text-purple-300/30 cursor-not-allowed"
                      : spread === m.id
                        ? "border-amber-500/60 bg-amber-500/10 text-amber-200"
                        : "border-[#4a2175] bg-[#180a26] text-purple-200/80 hover:border-amber-500/30"
                  }`}
                >
                  <span className="block text-sm">{m.label}</span>
                  <span className="block text-[10px] text-purple-300/60 mt-1">{locked ? "richiede il mazzo dei Maggiori" : m.sub}</span>
                </button>
                );
              })}
            </div>
          </div>
          )}
          {deck === "stesa26" && (
            <p className="text-[11px] text-purple-300/60 text-center leading-relaxed" data-testid="stesa26-metodo-note">
              Il metodo usa tutte le 26 carte del mazzo dedicato, tutte al dritto: dopo il taglio la prima carta
              resta Fuori quadro e le altre 25 formano il quadro originale in cinque file da cinque.
            </p>
          )}
          {deck === "stesa26" && (
            <p className="text-xs text-amber-200/90 text-center font-semibold tracking-wide" data-testid="stesa26-costo-note">
              Questa lettura consumerà 2 crediti.
            </p>
          )}
          {error && <p className="text-red-400 text-sm" data-testid="smazzo-error">{error}</p>}
          <button
            onClick={start}
            disabled={starting || (deck !== "stesa26" && !spread) || question.trim().length < 5 || (deck === "tarocchi" && !subset) || (spread === "siono" && !sionoNumber)}
            data-testid="smazzo-start-btn"
            className="gold-btn w-full py-3.5 text-sm flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Sparkles className="w-4 h-4" /> {mode === "mia" ? "Inserisci le carte estratte" : "Prendi il mazzo"}
          </button>
          <button
            onClick={() => { setPhase("scelta"); setMode(null); }}
            data-testid="smazzo-change-mode-btn"
            className="text-[11px] text-purple-300/50 hover:text-amber-300 transition-colors mx-auto"
          >
            Torna alla scelta della modalità
          </button>
        </div>
      )}

      {phase === "manuale" && (
        <div className="mystic-card p-5 sm:p-9" data-testid="manuale-entry">
          <p className="eyebrow text-center mb-2">Le mie stese</p>
          <p className="text-xs text-purple-300/60 text-center mb-1">
            {cfg.title} · {isS26 ? "Stesa 26" : (deck === "tarocchi" ? TAROCCHI_METHODS : METHODS).find((m) => m.id === spread)?.label}
          </p>
          <p className="text-sm text-purple-300/70 italic text-center mb-6">“{question}”</p>
          <p className="text-xs text-purple-200/70 text-center mb-6 leading-relaxed max-w-md mx-auto">
            Tocca ogni posizione e scegli la carta che hai realmente estratto dal tuo mazzo.
            {isS26
              ? " Nella Stesa 26 le carte sono tutte al dritto: indica prima il Fuori quadro, poi le 25 carte del quadro riga per riga."
              : " Indica prima le due carte uscite al taglio del tuo mazzo (lo smazzo), poi le carte della stesa. Puoi correggere ogni carta e cambiarne il verso prima di confermare."}
          </p>

          {deck === "tarocchi" && spread === "siono" && (
            <div className="mb-6">
              <p className="eyebrow mb-3 text-center">In quale fila è caduta la lama n. {sionoNumber}?</p>
              <div className="flex flex-wrap justify-center gap-1.5" data-testid="siono-row-picker">
                {[1, 2, 3, 4, 5, 6].map((r) => (
                  <button key={r} onClick={() => setSionoRow(r)} data-testid={`siono-row-${r}`}
                    className={`px-3.5 py-2 rounded-md text-xs font-cinzel border transition-colors ${sionoRow === r ? "border-amber-500/60 bg-amber-500/10 text-amber-200" : "border-[#4a2175] bg-[#180a26] text-purple-200/80 hover:border-amber-500/30"}`}>
                    Fila {r}
                  </button>
                ))}
                <button onClick={() => setSionoRow("acc")} data-testid="siono-row-acc"
                  className={`px-3.5 py-2 rounded-md text-xs font-cinzel border transition-colors ${sionoRow === "acc" ? "border-amber-500/60 bg-amber-500/10 text-amber-200" : "border-[#4a2175] bg-[#180a26] text-purple-200/80 hover:border-amber-500/30"}`}>
                  Accantonata
                </button>
              </div>
            </div>
          )}

          {isS26 ? (
            <div>
              <p className="eyebrow text-center mb-3">Fuori quadro</p>
              <div className="flex justify-center mb-6"><div className="w-[76px] sm:w-[88px]"><ManualSlot idx={0} /></div></div>
              <p className="eyebrow text-center mb-3">Il quadro, riga per riga</p>
              <div className="grid grid-cols-5 gap-1.5 max-w-md mx-auto" data-testid="manuale-quadro">
                {Array.from({ length: 25 }, (_, i) => <ManualSlot key={i + 1} idx={i + 1} />)}
              </div>
            </div>
          ) : (
            <div data-testid="manuale-slots">
              <p className="eyebrow text-center mb-3">Smazzo</p>
              <div className="flex flex-wrap justify-center gap-3 mb-7" data-testid="manuale-smazzo-slots">
                {manualSlots.slice(0, 2).map((_, i) => (
                  <div key={i} className="w-[92px] sm:w-[110px]">
                    <ManualSlot idx={i} />
                  </div>
                ))}
              </div>
              <p className="eyebrow text-center mb-3">Stesa</p>
              <div className="flex flex-wrap justify-center gap-3 mb-2" data-testid="manuale-stesa-slots">
                {manualSlots.slice(2).map((_, j) => (
                  <div key={j + 2} className="w-[92px] sm:w-[110px]">
                    <ManualSlot idx={j + 2} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {pickerSlot != null && (
            <div className="mt-6" data-testid="manuale-picker">
              <p className="eyebrow mb-3 text-center">Carta per: {slotLabel(pickerSlot)}</p>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5 max-h-80 overflow-y-auto pr-1">
                {deckCards.map((c) => {
                  const usedElsewhere = manualSlots.some((s, i) => s && s.id === c.id && i !== pickerSlot);
                  return (
                    <button key={c.id} disabled={usedElsewhere} onClick={() => assignCard(c.id)}
                      data-testid={`manuale-pick-${c.id}`}
                      className={`rounded-md border p-1.5 transition-colors text-center ${usedElsewhere ? "border-[#4a2175]/30 opacity-30" : "border-[#4a2175] bg-[#180a26] hover:border-amber-500/50"}`}>
                      {c.image_url ? (
                        <img src={c.image_url} alt={c.name} loading="lazy" decoding="async" className="w-full h-16 object-contain rounded-sm" draggable={false} />
                      ) : null}
                      <span className="block text-[9px] leading-tight text-purple-100 mt-1">{c.name}</span>
                      <span className="block text-[8px] text-purple-300/50">{c.suit}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {error && <p className="text-red-400 text-sm mt-4" data-testid="manuale-error">{error}</p>}
          <div className="flex flex-col sm:flex-row gap-3 mt-7">
            <button onClick={() => setPhase("form")} data-testid="manuale-back-btn"
              className="text-xs text-purple-300/60 hover:text-amber-300 border border-[#4a2175] rounded-full px-5 py-2.5 transition-colors">
              Indietro
            </button>
            <button onClick={confirmManual} disabled={!manualComplete || starting} data-testid="manuale-confirm-btn"
              className="gold-btn flex-1 py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-50">
              <Sparkles className="w-4 h-4" /> Conferma la stesa
            </button>
          </div>
        </div>
      )}

      {(phase === "orienta" || phase === "ricomponi") && (
        <div className="mystic-card p-5 sm:p-8 text-center" data-testid="smazzo-orienta">
          <p className="text-purple-200/80 text-sm mb-2">Appoggia il dito sulle carte e compi tre giri lenti e delicati.</p>
          <p className="font-cinzel text-amber-300/80 text-xs tracking-[0.2em] uppercase mb-6" data-testid="smazzo-giri-progress">
            Giro {giri} di 3
          </p>
          <div
            ref={pileBoxRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            className="relative w-full max-w-xl h-80 mx-auto touch-none cursor-pointer select-none overflow-hidden rounded-lg"
            data-testid="smazzo-orienta-area"
          >
            {deckRef.current.map((c) => (
              <CardBack
                key={c.id}
                innerRef={(el) => (cardElsRef.current[c.id] = el)}
                className="absolute top-0 left-0 w-[46px] h-[66px]"
              />
            ))}
          </div>
        </div>
      )}

      {phase === "mischia" && (
        <div className="mystic-card p-7 sm:p-9 text-center" data-testid="smazzo-mischia">
          <p className="text-purple-200/80 text-sm mb-8">Le carte si stanno mischiando. Premi STOP quando senti che è il momento.</p>
          <div className="relative h-52 flex items-center justify-center gap-0 overflow-visible" key={tick}>
            <div className="shuffle-left flex flex-col -space-y-[84px]" style={{ zIndex: 1 }}>
              {orderNow.slice(0, pileHalf).slice(-8).map((c) => (
                <CardBack key={c.id} className="w-[64px] h-[96px]" />
              ))}
            </div>
            <div className="shuffle-right flex flex-col -space-y-[84px] -ml-10" style={{ zIndex: 2 }}>
              {orderNow.slice(pileHalf).slice(-8).map((c) => (
                <CardBack key={c.id} className="w-[64px] h-[96px]" />
              ))}
            </div>
          </div>
          <button
            onClick={() => setPhase("smazzo")}
            data-testid="smazzo-stop-btn"
            className="gold-btn mt-8 px-10 py-3.5 text-sm flex items-center justify-center gap-2 mx-auto"
          >
            <Square className="w-4 h-4" /> STOP
          </button>
        </div>
      )}

      {phase === "smazzo" && (
        <div className="mystic-card p-7 sm:p-9 text-center" data-testid="smazzo-taglio-scelta">
          <p className="text-purple-200/80 text-sm mb-8">Scegli il punto in cui tagliare il mazzo.</p>
          <div className="overflow-x-auto pb-4" data-testid="smazzo-ribbon">
            <div className="flex w-max mx-auto px-6">
              {orderNow.map((c, i) => (
                <button
                  key={c.id}
                  onClick={() => beginCut(i)}
                  disabled={i < 1 || i > n - 2}
                  data-testid={`smazzo-cut-${i}`}
                  className={`w-[56px] h-[84px] rounded-md overflow-hidden bg-[#1b0b2e] flex-shrink-0 -ml-[38px] first:ml-0 transition-all duration-200 ${
                    i < 1 || i > n - 2 ? "opacity-50" : "hover:-translate-y-1.5 hover:ring-2 hover:ring-amber-400/70"
                  }`}
                  style={{ zIndex: i }}
                >
                  <img src="/dorso-arcana.jpg" alt="" draggable={false} className="w-full h-full object-fill select-none pointer-events-none block" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {phase === "taglio" && cutIds.length >= 1 && (
        <div className="mystic-card p-7 sm:p-9 text-center" data-testid="smazzo-taglio-anim">
          <p className="text-purple-200/80 text-sm mb-8">{isS26 ? "Il taglio: la carta in cima resta fuori dal quadro." : "Lo smazzo."}</p>
          {isS26 ? (
            <div className="flex items-end justify-center gap-10">
              <div className="cut-packet-left flex flex-col -space-y-[80px]" data-testid="smazzo-packet-top">
                {orderNow.slice(0, cutIndex).slice(-5).map((c) => (
                  <CardBack key={c.id} className="w-[64px] h-[96px]" />
                ))}
              </div>
              <div className="cut-packet-right flex flex-col -space-y-[80px]" data-testid="smazzo-packet-bottom">
                <div className="cut-reveal w-[64px]" data-testid="stesa26-fuori-quadro-reveal">
                  <CardFace card={{ ...cardMapRef.current[cutIds[0]], reversed: false }} deck={deck} />
                </div>
                {orderNow.slice(cutIndex + 1).slice(-5).map((c) => (
                  <CardBack key={c.id} className="w-[64px] h-[96px]" />
                ))}
              </div>
            </div>
          ) : (
          <div className="flex items-end justify-center gap-10">
            <div className="cut-packet-left flex flex-col -space-y-[80px]" data-testid="smazzo-packet-top">
              {orderNow.slice(0, cutIndex - 1).slice(-5).map((c) => (
                <CardBack key={c.id} className="w-[64px] h-[96px]" />
              ))}
              <div className="cut-reveal w-[64px]" data-testid="smazzo-carta-rivelata-1">
                <CardFace card={{ ...cardMapRef.current[cutIds[0]], reversed: revById[cutIds[0]] }} deck={deck} />
              </div>
            </div>
            <div className="cut-packet-right flex flex-col -space-y-[80px]" data-testid="smazzo-packet-bottom">
              <div className="cut-reveal w-[64px]" data-testid="smazzo-carta-rivelata-2">
                <CardFace card={{ ...cardMapRef.current[cutIds[1]], reversed: revById[cutIds[1]] }} deck={deck} />
              </div>
              {orderNow.slice(cutIndex + 1).slice(-5).map((c) => (
                <CardBack key={c.id} className="w-[64px] h-[96px]" />
              ))}
            </div>
          </div>
          )}
        </div>
      )}

      {(phase === "stesa" || phase === "lettura" || phase === "done") && (
        <div className="space-y-8">
          <div className="mystic-card p-7 sm:p-9" data-testid="smazzo-risultato">
            {mode === "mia" && (
              <p className="font-cinzel text-[10px] tracking-[0.3em] uppercase text-amber-300/70 text-center mb-3" data-testid="lettura-mie-stese-label">Le mie stese</p>
            )}
            <p className="text-sm text-purple-300/60 italic mb-6 text-center">“{question}”</p>
            {isS26 && (
            <div className="mb-8" data-testid="stesa26-zone">
              <p className="eyebrow text-center mb-4">Fuori quadro</p>
              <div className="flex justify-center">
                {drawnIds.slice(0, Math.min(dealt, 1)).map((id) => {
                  const base = cards[0] || { ...cardMapRef.current[id], reversed: false, position: "Fuori quadro" };
                  return (
                    <div key={id} className="w-[76px] sm:w-[88px] ring-2 ring-amber-400/70 rounded-md" data-testid="stesa26-fuori-quadro">
                      <CardFace card={base} deck={deck} />
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-purple-300/50 text-center mt-2">La 26ª carta: resta fuori dal quadro</p>
            </div>
            )}
            {!isS26 && (
            <div className="mb-8" data-testid="smazzo-zone">
              <p className="eyebrow text-center mb-4">Smazzo</p>
              <div className="flex flex-row justify-center gap-3">
                {drawnIds.slice(0, Math.min(dealt, 2)).map((id, i) => {
                  const base = cards[i] || { ...cardMapRef.current[id], reversed: revById[id], position: "" };
                  return (
                    <div key={id} className="w-[92px] sm:w-[110px]" data-testid={`smazzo-card-${i}`}>
                      <CardFace card={base} deck={deck} />
                      {!base.image_url && (
                      <span className={`block mx-auto w-fit mt-1.5 text-[10px] px-2 py-0.5 rounded-full border ${
                        base.reversed ? "border-fuchsia-500/40 text-fuchsia-300" : "border-amber-500/40 text-amber-300"
                      }`}>
                        {base.reversed ? "Rovesciata" : "Dritta"}
                      </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            )}
            <div className="pt-6 border-t border-amber-500/15">
              <p className="eyebrow text-center mb-4">{isS26 ? "Il quadro" : "Stesa"}</p>
              <div
                className={
                  isS26
                    ? "grid grid-cols-5 gap-1.5 max-w-md mx-auto"
                    : spread === "croce"
                      ? "grid grid-cols-3 gap-3 max-w-md mx-auto"
                      : "flex flex-row flex-wrap justify-center gap-3"
                }
                data-testid="smazzo-spread"
              >
                {drawnIds.slice(firstOut, dealt).map((id, j) => {
                  const i = j + firstOut;
                  const base = cards[i] || { ...cardMapRef.current[id], reversed: revById[id], position: "" };
                  const cell = spread === "croce" ? CROCE_CELL[j] : "";
                  return (
                    <div
                      key={id}
                      className={isS26 ? "w-full" : `${spread === "croce" ? cell : ""} w-[92px] sm:w-[110px]`}
                      data-testid={`stesa-card-${j}`}
                    >
                      <CardFace card={base} deck={deck} />
                      {!isS26 && (
                      <p className="text-[10px] uppercase tracking-wider text-purple-300/50 mt-1.5 text-center">{base.position}</p>
                      )}
                      {!isS26 && !base.image_url && (
                      <span className={`block mx-auto w-fit mt-1 text-[10px] px-2 py-0.5 rounded-full border ${
                        base.reversed ? "border-fuchsia-500/40 text-fuchsia-300" : "border-amber-500/40 text-amber-300"
                      }`}>
                        {base.reversed ? "Rovesciata" : "Dritta"}
                      </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {(phase === "lettura" || phase === "done") && (
              <div className="mt-8 pt-6 border-t border-amber-500/15">
                <p className="eyebrow mb-4">L'interpretazione</p>
                <div className="text-purple-100/85 leading-loose text-sm sm:text-base whitespace-pre-line" data-testid="smazzo-interpretation">
                  <MarkdownText text={text} />
                  {phase === "lettura" && <span className="inline-block w-2 h-4 bg-amber-400/70 ml-1 animate-pulse" />}
                </div>
                {phase === "lettura" && !text && <p className="text-purple-300/50 text-sm">Arcana sta interpretando le carte…</p>}
                {error && <p className="text-red-400 text-sm mt-2" data-testid="smazzo-error">{error}</p>}
                {phase === "done" && <ReadingAlerts avvisi={avvisi} />}
                <FiloOffer visible={phase === "done" && !!text} />
              </div>
            )}
            {phase === "done" && text && (
              <div className="mt-4 flex justify-center gap-2 flex-wrap">
                <CopyReadingButton text={text} testid="copy-reading-smazzo" />
                {readingId && (
                  <DeleteReadingButton testid="delete-reading-smazzo" onDelete={async () => {
                    await api.delete(`/readings/${readingId}`);
                    toast.success("Testo eliminato.");
                    reset();
                  }} />
                )}
              </div>
            )}
            {phase === "done" && text && (
              <FeedbackStelle sourceType="lettura" sourceId={readingId} testid="smazzo" />
            )}
            {!embedded && phase === "done" && text && deck === "baraja" && !crudaText && !crudaDeclined && (
              <div className="mt-6 pt-5 border-t border-amber-500/15 text-center" data-testid="cruda-cta">
                <p className="font-display text-lg text-amber-100 mb-2">Vuoi conoscere anche la versione più cruda della lettura?</p>
                <p className="text-[11px] text-purple-300/60 mb-4 leading-relaxed">
                  Una seconda lettura sulle stesse identiche carte, con un linguaggio più diretto e senza attenuazioni.
                  È gratuita: nessun credito aggiuntivo, solo se la desideri.
                </p>
                <div className="flex justify-center gap-3 flex-wrap">
                  <button onClick={askCruda} disabled={crudaBusy} data-testid="cruda-yes"
                    className="gold-btn px-5 py-2.5 text-[11px] inline-flex items-center gap-2 disabled:opacity-50">
                    {crudaBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    {crudaBusy ? "Arcana sta leggendo…" : "Sì, mostramela"}
                  </button>
                  <button onClick={() => setCrudaDeclined(true)} data-testid="cruda-no"
                    className="text-[11px] text-purple-300/60 hover:text-amber-300 border border-[#4a2175] rounded-full px-5 py-2.5 transition-colors">
                    No, grazie
                  </button>
                </div>
                {crudaError && <p className="text-red-400 text-sm mt-3" data-testid="cruda-error">{crudaError}</p>}
              </div>
            )}
            {crudaText && (
              <div className="mt-6 pt-5 border-t border-amber-500/15" data-testid="cruda-result">
                <p className="eyebrow mb-4 text-center">Variante Cruda</p>
                <div className="text-purple-100/85 leading-loose text-sm sm:text-base whitespace-pre-line" data-testid="cruda-text">
                  <MarkdownText text={crudaText} />
                </div>
              </div>
            )}
            {!embedded && phase === "done" && text && figura && (
              <FiguraInvite onOpen={() => setFiguraOpen(true)} onDismiss={() => setFigura(null)} />
            )}
            {!embedded && figuraOpen && figura && (
              <FiguraRiconosciuta figura={figura} readingId={readingId} onClose={() => { setFiguraOpen(false); setFigura(null); }} />
            )}
            {phase === "done" && text && readingId && (
              <div className="mt-6 pt-5 border-t border-amber-500/15" data-testid="approfondisci-box">
                <p className="eyebrow mb-3 text-center">Approfondisci con Arcana</p>
                <textarea
                  value={apprQuestion}
                  onChange={(e) => setApprQuestion(e.target.value)}
                  rows={2}
                  maxLength={800}
                  placeholder="Scrivi qui cosa vuoi approfondire sulla lettura..."
                  data-testid="approfondisci-input"
                  className="input-dark"
                  disabled={apprBusy || apprRemaining <= 0}
                />
                <p className="text-xs text-purple-300/60 mt-2 text-center" data-testid="approfondisci-hint">
                  È emerso qualcosa su cui vuoi chiedere di più?
                </p>
                <div className="flex items-center justify-between gap-3 flex-wrap mt-3">
                  <p className="text-[11px] text-purple-300/50" data-testid="approfondisci-remaining">
                    {apprRemaining > 0
                      ? `${apprRemaining} di 2 approfondimenti inclusi in questa lettura: Arcana risponde sulle carte già estratte, senza una nuova stesa.`
                      : "Hai usato i 2 approfondimenti inclusi in questa lettura."}
                  </p>
                  <button
                    onClick={sendAppr}
                    disabled={apprBusy || apprQuestion.trim().length < 3 || apprRemaining <= 0}
                    data-testid="approfondisci-send"
                    className="gold-btn px-5 py-2 text-[11px] inline-flex items-center gap-2 whitespace-nowrap disabled:opacity-50"
                  >
                    {apprBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    {apprBusy ? "Arcana sta rispondendo…" : "Invia la domanda"}
                  </button>
                </div>
                {apprThread.length > 0 && (
                  <div className="mt-4 space-y-3" data-testid="approfondisci-thread">
                    {apprThread.map((m, i) => (
                      <div key={i} className="space-y-2" data-testid={`approfondisci-msg-${i}`}>
                        <p className="text-sm text-purple-300/70 italic">“{m.q}”</p>
                        <div className="text-purple-100/85 leading-loose text-sm whitespace-pre-line rounded-xl bg-[#0f0617] border border-[#4a2175] p-4">
                          <MarkdownText text={m.a} />
                          {m.streaming && <span className="inline-block w-2 h-4 bg-amber-400/70 ml-1 animate-pulse" />}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {phase === "done" && text && (
              <div className="mt-6 pt-5 border-t border-amber-500/15" data-testid="smazzo-next-question">
                <p className="eyebrow mb-2 text-center">Vuoi fare una nuova domanda alle carte?</p>
                <p className="text-xs text-purple-300/60 mb-4 leading-relaxed text-center">
                  Fai un'altra domanda collegata a ciò che è emerso: partirà una nuova consultazione
                  con il normale procedimento e le consuete regole sui crediti, e Arcana la interpreterà
                  alla luce di questa lettura.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    value={nextQuestion}
                    onChange={(e) => setNextQuestion(e.target.value)}
                    maxLength={600}
                    placeholder="Es. «Quando succederà?»"
                    data-testid="smazzo-next-question-input"
                    className="input-dark flex-1"
                  />
                  <button
                    onClick={startLinkedQuestion}
                    disabled={nextQuestion.trim().length < 5}
                    data-testid="smazzo-next-question-btn"
                    className="gold-btn px-5 py-2 text-[11px] whitespace-nowrap disabled:opacity-50"
                  >
                    Fai la domanda
                  </button>
                </div>
              </div>
            )}
            {phase === "done" && (
              <ReadingActions
                readingId={readingId}
                showAssistant={false}
                onReset={reset}
                resetLabel="Nuova stesa"
                testid="smazzo"
              />
            )}
          </div>
          {phase === "done" && (
            <p className="text-xs text-purple-300/50 text-center leading-relaxed">
              La lettura ha finalità di riflessione e orientamento personale. Eventuali tempistiche indicate
              sono puramente indicative e non costituiscono una certezza.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
